import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  PerformancePage,
  PerformancePageOverview,
} from 'api/new-analytics/types';
import ReportMetricCardsRow from './ReportMetricCardsRow';
import { getMetricDisplay } from 'app/modules/analytics/modules/performance/utils/getPerformanceMetricDisplay';
import { formatReportMetricLabel } from 'app/modules/analytics/modules/reports/utils/formatReportMetricLabel';
import type { ReportPreviewPageModel } from 'app/modules/analytics/modules/reports/utils/buildReportPreviewPages';
import ReportPreviewMetricCharts from './ReportPreviewMetricCharts';
import ReportPreviewSectionBlock from './ReportPreviewSectionBlock';
import ReportPreviewSectionNote from './ReportPreviewSectionNote';
import ReportPreviewTopPostsSection from './ReportPreviewTopPostsSection';

interface Props {
  page: ReportPreviewPageModel;
  performancePages: PerformancePage[];
  performanceOverview: PerformancePageOverview | undefined;
  isPerformanceLoading: boolean;
  getPerformanceMetricValue: (metricKey: string) => string;
  performanceEmptyMessage?: string;
  isContentLoading: boolean;
  getContentMetricValue: (metricKey: string) => string;
  isContentPostsLoading?: boolean;
  performanceNote?: string | null;
  contentNote?: string | null;
  showPerformanceNote?: boolean;
  showContentNote?: boolean;
}

const GRID_COLS_CLASS: Record<number, string> = {
  1: 'tw-grid tw-grid-cols-1',
  2: 'tw-grid tw-grid-cols-2',
  3: 'tw-grid tw-grid-cols-3',
  4: 'tw-grid tw-grid-cols-4',
};

const ReportPreviewPageSections = ({
  page,
  performancePages,
  performanceOverview,
  isPerformanceLoading,
  getPerformanceMetricValue,
  isContentLoading,
  getContentMetricValue,
  isContentPostsLoading = false,
  performanceNote,
  contentNote,
  showPerformanceNote = false,
  showContentNote = false,
}: Props) => {
  const { t } = useTranslation();

  const performanceItems = useMemo(
    () =>
      page.performanceTileMetrics.map((metricKey) => ({
        title: formatReportMetricLabel(metricKey, 'performance', t),
        value: getMetricDisplay(performanceOverview, metricKey).value,
        badge: getMetricDisplay(performanceOverview, metricKey).badge,
      })),
    [page.performanceTileMetrics, performanceOverview, t],
  );

  const contentItems = useMemo(
    () =>
      page.contentMetrics.map((metricKey) => ({
        title: formatReportMetricLabel(metricKey, 'content', t),
        value: getContentMetricValue(metricKey),
      })),
    [page.contentMetrics, getContentMetricValue, t],
  );

  const hasPerformanceMetrics =
    page.chartMetrics.length > 0 || performanceItems.length > 0;

  const hasContentMetrics = contentItems.length > 0;

  return (
    <div className="tw-space-y-6">
      {hasPerformanceMetrics ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewPerformance')}
        >
          {page.chartMetrics.length > 0 ? (
            <ReportPreviewMetricCharts
              chartMetrics={page.chartMetrics}
              pages={performancePages}
              overview={performanceOverview}
              isLoading={isPerformanceLoading}
            />
          ) : null}
          {performanceItems.length > 0 ? (
            <ReportMetricCardsRow
              isLoading={isPerformanceLoading}
              skeletonCount={performanceItems.length}
              gridClassName={
                GRID_COLS_CLASS[Math.min(2, performanceItems.length) || 1] ??
                GRID_COLS_CLASS[2]
              }
              items={performanceItems}
            />
          ) : null}
        </ReportPreviewSectionBlock>
      ) : null}
      {showPerformanceNote ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewPerformance')}
        >
          <ReportPreviewSectionNote note={performanceNote} />
        </ReportPreviewSectionBlock>
      ) : null}
      {hasContentMetrics ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewContent')}
        >
          <ReportMetricCardsRow
            isLoading={isContentLoading}
            skeletonCount={contentItems.length}
            gridClassName={
              GRID_COLS_CLASS[Math.min(2, contentItems.length) || 1] ??
              GRID_COLS_CLASS[2]
            }
            items={contentItems}
          />
        </ReportPreviewSectionBlock>
      ) : null}
      {showContentNote ? (
        <ReportPreviewSectionBlock
          title={t('analyticsReports.createReport.previewContent')}
        >
          <ReportPreviewSectionNote note={contentNote} />
        </ReportPreviewSectionBlock>
      ) : null}
      {(page.postChunk?.length ?? 0) > 0 ? (
        <ReportPreviewTopPostsSection
          posts={page.postChunk ?? []}
          isLoading={isContentPostsLoading}
        />
      ) : null}
    </div>
  );
};

export default ReportPreviewPageSections;
